/**
 * FlahaSOIL application SEO — Flaha Agri Tech + hybrid product-domain model.
 *
 * Authority:
 *  - docs/adr/0001-hybrid-public-product-seo.md
 *  - docs/FlahaSOIL_Product_SEO_Scientific_Content_Search_Architecture_Framework.md
 *  - flaha.org lib/seo.js patterns (locale, parent org, SoftwareApplication)
 *
 * Hybrid split:
 *  - flaha.org/products/flahasoil/  → corporate gateway (concise)
 *  - flahasoil.flaha.org/          → product domain (public product grows;
 *                                    authenticated app never indexable)
 */

import {
	BRAND_DIVISION,
	BRAND_ORG,
	BRAND_PRODUCT_LABEL,
	BRAND_PRODUCT_ROLE,
	BRAND_TAGLINE,
	BRAND_WORDMARK,
} from "./brand";

/** Canonical application origin (production). */
export const APP_SITE_URL = "https://flahasoil.flaha.org";

/** Corporate marketing site (parent authority). */
export const CORPORATE_SITE_URL = "https://flaha.org";

/** Canonical public product page on the corporate site. */
export const PRODUCT_MARKETING_URL = `${CORPORATE_SITE_URL}/products/flahasoil`;

/** Organization display name for OG site_name / JSON-LD. */
export const SITE_NAME = BRAND_ORG;

/** Locale used across Flaha properties (flaha.org standard). */
export const SITE_LOCALE = "en_QA";

/** Default theme colour (Flaha Green from brand guidelines). */
export const THEME_COLOR = "#3D7A42";

/** Public logo paths served from this app's origin (synced from flaha.org). */
export const BRAND_ASSET_PATHS = {
	orgLogo: "/logos/flaha-main.png",
	divisionLogo: "/logos/flaha-pa.png",
	productLogoSvg: "/logos/apps/flahasoil.svg",
	productLogoPng: "/brand/flahasoil-logo.png",
	ogImage: "/images/og/flahasoil.jpg",
	appleTouchIcon: "/apple-touch-icon.png",
	iconSvg: "/icon.svg",
	favicon: "/favicon.ico",
	favicon32: "/favicon-32x32.png",
	favicon16: "/favicon-16x16.png",
} as const;

/**
 * Default document title (framework §9.1 + parent brand).
 * Never include internal build labels (e.g. "v2").
 */
export const SEO_DEFAULT_TITLE = `${BRAND_WORDMARK} | Scientific Soil Analysis & Interpretation Platform | ${BRAND_ORG}`;

/**
 * Meta description (framework §9.2, product-domain wording).
 */
export const SEO_DEFAULT_DESCRIPTION =
	"Convert laboratory and field soil data into traceable assessments of soil texture, water retention, salinity, sodicity, CEC and agronomic limitations.";

/** Social / OG share title. */
export const SEO_OG_TITLE = `${BRAND_WORDMARK}: soil intelligence`;

/** Social / OG share description. */
export const SEO_OG_DESCRIPTION =
	"Interpret physics, chemistry, salinity and sodicity—structured for agriculture and landscape decisions in arid climates.";

/** Keywords (supporting; not a ranking primary). */
export const SEO_KEYWORDS = [
	"FlahaSOIL",
	"Flaha Agri Tech",
	"Flaha PA",
	"soil intelligence",
	"soil analysis",
	"soil interpretation",
	"soil physics",
	"soil chemistry",
	"salinity",
	"sodicity",
	"precision agriculture",
	"Qatar",
].join(", ");

/**
 * Paths that may be indexed today (P0 public surface).
 * Expand only when a real public page ships with crawlable content.
 */
export const INDEXABLE_PATHS = new Set<string>(["/", "/standards"]);

export type RobotsDirective = {
	index: boolean;
	follow: boolean;
	/** Content attribute value for <meta name="robots"> */
	content: string;
};

/** Normalize pathname for SEO matching (strip trailing slash except root). */
export function normalizeSeoPath(pathname: string): string {
	if (!pathname || pathname === "/") return "/";
	const trimmed = pathname.replace(/\/+$/, "");
	return trimmed === "" ? "/" : trimmed;
}

/**
 * Indexation policy (framework §16 + ADR 0001).
 * Default is noindex — only explicitly public product pages are indexable.
 */
export function resolveRobotsForPath(pathname: string): RobotsDirective {
	const path = normalizeSeoPath(pathname);
	if (INDEXABLE_PATHS.has(path)) {
		return {
			index: true,
			follow: true,
			content: "index, follow, max-image-preview:large",
		};
	}
	return {
		index: false,
		follow: true,
		content: "noindex, follow",
	};
}

/** Absolute URL helper for this application origin. */
export function absoluteAppUrl(path = "/"): string {
	const p =
		path === "" || path === "/"
			? ""
			: path.startsWith("/")
				? path
				: `/${path}`;
	return `${APP_SITE_URL}${p}`;
}

/**
 * Document title helper.
 * Template: `{page} | FlahaSOIL | Flaha Agri Tech` (or default home title).
 */
export function buildDocumentTitle(pageTitle?: string | null): string {
	const trimmed = pageTitle?.trim() ?? "";
	const normalized = trimmed.replace(/\s*v2\b/gi, "").trim();

	if (
		!normalized ||
		normalized === BRAND_WORDMARK ||
		normalized === BRAND_PRODUCT_LABEL ||
		normalized === "Page not found" ||
		normalized.startsWith(`${BRAND_WORDMARK} —`) ||
		normalized.startsWith(`${BRAND_WORDMARK} -`) ||
		normalized.startsWith(`${BRAND_WORDMARK} |`)
	) {
		// 404 keeps a distinct title
		if (normalized === "Page not found") {
			return `Page not found | ${BRAND_WORDMARK} | ${BRAND_ORG}`;
		}
		return SEO_DEFAULT_TITLE;
	}

	return `${normalized} | ${BRAND_WORDMARK} | ${BRAND_ORG}`;
}

/** Default Open Graph image (1200×630 product card — flaha.org Template P). */
export function defaultOgImage() {
	return {
		url: absoluteAppUrl(BRAND_ASSET_PATHS.ogImage),
		width: 1200,
		height: 630,
		alt: `${BRAND_WORDMARK} — ${BRAND_PRODUCT_ROLE}`,
	};
}

export function organizationJsonLd() {
	return {
		"@context": "https://schema.org",
		"@type": "Organization",
		"@id": `${CORPORATE_SITE_URL}/#organization`,
		name: BRAND_ORG,
		legalName: BRAND_ORG,
		url: CORPORATE_SITE_URL,
		logo: `${CORPORATE_SITE_URL}/logos/flaha-main.png`,
		email: "info@flaha.org",
		areaServed: { "@type": "Country", name: "Qatar" },
		department: [
			{
				"@type": "Organization",
				name: BRAND_DIVISION,
				url: `${CORPORATE_SITE_URL}/flaha-pa`,
				description: "Precision agriculture and digital agronomy",
			},
		],
	};
}

export function softwareApplicationJsonLd() {
	return {
		"@context": "https://schema.org",
		"@type": "SoftwareApplication",
		name: BRAND_WORDMARK,
		description: SEO_DEFAULT_DESCRIPTION,
		url: APP_SITE_URL,
		applicationCategory: "BusinessApplication",
		operatingSystem: "Web",
		image: absoluteAppUrl(BRAND_ASSET_PATHS.ogImage),
		featureList: [
			"Soil physics interpretation",
			"Soil chemistry interpretation",
			"Salinity and sodicity assessment",
			"Professional soil reporting",
		],
		provider: {
			"@type": "Organization",
			name: BRAND_ORG,
			url: CORPORATE_SITE_URL,
			logo: `${CORPORATE_SITE_URL}/logos/flaha-main.png`,
		},
		isPartOf: {
			"@type": "WebSite",
			name: BRAND_DIVISION,
			url: `${CORPORATE_SITE_URL}/flaha-pa`,
		},
		sameAs: [PRODUCT_MARKETING_URL],
		offers: {
			"@type": "Offer",
			availability: "https://schema.org/OnlineOnly",
			price: "0",
			priceCurrency: "QAR",
			description: "Demonstration and commercial access by enquiry",
		},
	};
}

export function websiteJsonLd() {
	return {
		"@context": "https://schema.org",
		"@type": "WebSite",
		"@id": `${APP_SITE_URL}/#website`,
		name: BRAND_PRODUCT_LABEL,
		url: APP_SITE_URL,
		description: BRAND_TAGLINE,
		inLanguage: "en",
		publisher: { "@id": `${CORPORATE_SITE_URL}/#organization` },
	};
}
